import { useState, useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';

export interface LecturerSalaryDetail {
  lecturer_id: string;
  lecturer_name: string;
  group_id: string;
  group_name: string;
  phase_id: string;
  phase_name: string;
  student_count: number;
  lessons_taught: number;
  per_lesson_rate: number;
  expected_salary: number;
  paid_salary: number;
  pending_salary: number;
  remaining_salary: number;
  payment_status: string;
  last_lesson_date: string;
  phase_completed: boolean;
  twelfth_lesson_date: string | null;
}

interface LecturerSummary {
  lecturer_id: string;
  lecturer_name: string;
  total_expected_salary: number;
  total_paid_salary: number;
  total_pending_salary: number;
  phase_count: number;
}

export const useOptimizedLecturerSalary = () => {
  const { toast } = useToast();
  const { user, isAdmin } = useAuth();
  const [details, setDetails] = useState<LecturerSalaryDetail[]>([]);
  const [summaries, setSummaries] = useState<LecturerSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [detailsMap, setDetailsMap] = useState<{ [lecturerId: string]: LecturerSalaryDetail[] }>({});
  const channelRef = useRef<any>(null);

  const fetchLecturerSalaryData = async () => {
    try {
      console.log('🚀 Fetching optimized lecturer salary data...');
      setLoading(true);

      // Single comprehensive query using the optimized RPC function
      const { data, error } = await supabase.rpc('get_lecturer_salary_data');
      
      if (error) {
        console.error('❌ RPC error:', error);
        console.error('❌ Error details:', {
          message: error.message,
          code: error.code,
          hint: error.hint,
          details: error.details
        });
        throw error;
      }

      console.log('✅ Raw lecturer salary data received:', data?.length || 0, 'records');

      if (!data || data.length === 0) {
        setDetails([]);
        setSummaries([]);
        setDetailsMap({});
        return;
      }

      // Process data using the new standardized RPC structure
      let detailRecords: LecturerSalaryDetail[] = data.map(row => ({
        lecturer_id: row.lecturer_id,
        lecturer_name: row.lecturer_name,
        group_id: row.group_id,
        group_name: row.group_name,
        phase_id: row.phase_id,
        phase_name: row.phase_name,
        student_count: Number(row.student_count),
        lessons_taught: Number(row.lessons_taught),
        per_lesson_rate: row.per_lesson_rate,
        expected_salary: row.expected_salary,
        paid_salary: row.paid_salary,
        pending_salary: row.pending_salary || 0,
        remaining_salary: row.remaining_salary,
        last_lesson_date: row.last_lesson_date ? new Date(row.last_lesson_date).toISOString().split('T')[0] : '',
        payment_status: row.payment_status,
        phase_completed: row.phase_completed,
        twelfth_lesson_date: row.twelfth_lesson_date ? new Date(row.twelfth_lesson_date).toISOString().split('T')[0] : null
      }));

      // Filter data for non-admin users: only show their own salary data
      if (!isAdmin && user?.id) {
        detailRecords = detailRecords.filter(record => record.lecturer_id === user.id);
      }

      // Create details map grouped by lecturer
      const detailsMapResult: { [lecturerId: string]: LecturerSalaryDetail[] } = {};
      detailRecords.forEach(detail => {
        if (!detailsMapResult[detail.lecturer_id]) {
          detailsMapResult[detail.lecturer_id] = [];
        }
        detailsMapResult[detail.lecturer_id].push(detail);
      });

      // Calculate summaries efficiently using reduce
      const summaryMap = detailRecords.reduce((acc, detail) => {
        const lecturerId = detail.lecturer_id;
        
        if (!acc[lecturerId]) {
          acc[lecturerId] = {
            lecturer_id: lecturerId,
            lecturer_name: detail.lecturer_name,
            total_expected_salary: 0,
            total_paid_salary: 0,
            total_pending_salary: 0,
            phase_count: 0
          };
        }

        acc[lecturerId].total_expected_salary += detail.expected_salary;
        acc[lecturerId].total_paid_salary += detail.paid_salary;
        acc[lecturerId].total_pending_salary += detail.pending_salary;
        acc[lecturerId].phase_count += 1;

        return acc;
      }, {} as { [lecturerId: string]: LecturerSummary });

      const summaryArray = Object.values(summaryMap);

      console.log('✅ Processed data:', {
        details: detailRecords.length,
        summaries: summaryArray.length,
        lecturers: Object.keys(detailsMapResult).length
      });

      // Update state in single batch
      setDetails(detailRecords);
      setSummaries(summaryArray);
      setDetailsMap(detailsMapResult);

    } catch (error) {
      console.error('❌ Error fetching lecturer salary data:', error);
      toast({
        title: "Սխալ",
        description: "Չհաջողվեց բեռնել դասախոսների աշխատավարձի տվյալները",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const markSalaryAsPaid = async (phaseId: string, groupId: string, lecturerId: string) => {
    try {
      console.log('🔄 Marking salary as paid:', { phaseId, groupId, lecturerId });

      const { error } = await supabase.rpc('mark_lecturer_salary_paid', {
        phase_id_param: phaseId,
        group_id_param: groupId,
        lecturer_id_param: lecturerId
      });

      if (error) {
        console.error('❌ RPC Error:', error);
        throw error;
      }

      console.log('✅ Salary marked as paid successfully');
      toast({
        title: "Հաջողություն",
        description: "Աշխատավարձը նշանակվել է որպես վճարված",
      });

      // Refresh data after successful payment
      await fetchLecturerSalaryData();

    } catch (error: any) {
      console.error('❌ Error in markSalaryAsPaid:', error);
      const message = error.message || 'Անհայտ սխալ';
      
      toast({
        title: "Սխալ",
        description: `Չհաջողվեց նշանակել վճարված՝ ${message}`,
        variant: "destructive",
      });
      
      throw error;
    }
  };

  const formatAmount = (amount: number): string => {
    return new Intl.NumberFormat('hy-AM', {
      style: 'currency',
      currency: 'AMD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  // Setup real-time subscriptions with optimized debouncing
  useEffect(() => {
    const initializeData = async () => {
      await fetchLecturerSalaryData();
    };

    // Debounced refresh function
    let debounceTimeout: NodeJS.Timeout;
    const debouncedRefresh = () => {
      if (debounceTimeout) {
        clearTimeout(debounceTimeout);
      }
      debounceTimeout = setTimeout(() => {
        console.log('🔄 Real-time refresh triggered');
        fetchLecturerSalaryData();
      }, 1500); // Reduced debounce time for better responsiveness
    };

    // Setup efficient real-time subscription
    const setupRealtimeSubscription = () => {
      console.log('📡 Setting up optimized realtime subscription');
      
      // Single channel for multiple table changes
      channelRef.current = supabase
        .channel('lecturer-salary-optimized')
        .on(
          'postgres_changes',
          { event: '*', schema: 'public', table: 'attendance' },
          debouncedRefresh
        )
        .on(
          'postgres_changes',
          { event: '*', schema: 'public', table: 'lessons' },
          debouncedRefresh
        )
        .on(
          'postgres_changes',
          { event: '*', schema: 'public', table: 'financial_records' },
          debouncedRefresh
        )
        .subscribe((status) => {
          console.log('📡 Realtime subscription status:', status);
        });
    };

    initializeData();
    setupRealtimeSubscription();

    // Cleanup function
    return () => {
      if (debounceTimeout) {
        clearTimeout(debounceTimeout);
      }
      if (channelRef.current) {
        console.log('🧹 Cleaning up realtime subscription');
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
  }, []);

  return {
    details,
    summaries,
    detailsMap,
    loading,
    fetchLecturerSalaryData,
    markSalaryAsPaid,
    formatAmount
  };
};